import { type SubscriptionTerm } from '@cx-sdk/subs';
import { REPUB_CONFIG, type SupportedRepubName } from '@cx-web/repub-config';
import { trackEvent } from '@cx-web/utils/dataLayer';

import { type Repub } from '@cx-modules/constants';
import { type BookmakerConfig } from '@cx-modules/types';

import {
  type BettingSplitsBet,
  type PlayerDataResult,
  type PremiumMarketMoversResult,
  type ValueBetResult,
} from '~/configuration/types';

export const trackEvBetClick = ({
  bookmaker,
  component,
  result,
}: {
  bookmaker: BookmakerConfig;
  component: string;
  result: ValueBetResult;
}) => {
  trackEvent('value_bet_click', {
    bookieName: bookmaker.bookmakerName,
    component,
    eventLabel: 'Value Bet Click',
    Market: result.marketDisplayName,
    'Player/Team': result.line ? result.line.name : result.betName,
    Props: result.marketPropType,
    Sport: result.categoryName,
    Subevent: result.subeventName,
    trend: result.sportsData.hitRates.last10,
  });
};

export const trackBettingSplitsBetClick = ({
  bet,
  bookmaker,
  categoryName,
  component,
  marketDisplayName,
  marketPropType,
  subeventName,
}: {
  bet: BettingSplitsBet;
  bookmaker: BookmakerConfig;
  categoryName: string;
  component: string;
  marketDisplayName: string;
  marketPropType: string;
  subeventName: string;
}) => {
  trackEvent('betting_splits_bet_click', {
    BestOdds: bet.oddsDecimal,
    bookieName: bookmaker.bookmakerName,
    component,
    eventLabel: 'Betting Splits Bet Click',
    Market: marketDisplayName,
    percentageOfBets: bet.betPercentage,
    percentageOfMoney: bet.stakePercentage,
    'Player/Team': bet.betName,
    Props: marketPropType,
    Sport: categoryName,
    Subevent: subeventName,
  });
};

export const trackTrendsBetClick = ({
  bestOdds,
  bookieName,
  component,
  league,
  market,
  playerOrTeam,
  sport,
  subevent,
  tooltip,
}: {
  bestOdds: string;
  bookieName: string;
  component: string;
  league: string;
  market: string;
  playerOrTeam: string;
  sport: string;
  subevent: string;
  tooltip: string;
}) => {
  trackEvent('trends_bet_click', {
    bestOdds,
    bookieName,
    component,
    eventLabel: 'Trends Bet Click',
    league,
    market,
    playerOrTeam,
    sport,
    subevent,
    tooltip,
  });
};

export const trackSeeAllClick = ({ component }: { component: string }) => {
  trackEvent('see_all', {
    component,
    eventLabel: 'See All Click',
  });
};

export const trackLandingPagePlansToggleClick = ({ selection }: { selection: 'monthly' | 'annual' }) => {
  trackEvent('plans_toggle', {
    eventLabel: 'Plans Toggle',
    selection,
  });
};

export const trackLandingFreeTrialClick = ({
  position,
  userHasCancelledSubscription,
}: {
  position: 1 | 2 | 3 | 4;
  userHasCancelledSubscription: boolean;
}) => {
  trackEvent(userHasCancelledSubscription ? 'subscribe_again' : 'try_free_trial', {
    eventLabel: userHasCancelledSubscription ? 'Subscribe Again' : 'Try Free Trial',
    position,
  });
};

export const trackLandingSubscriptionClick = ({
  isUpgrade = false,
  price,
  repub,
  selection,
  term,
}: {
  isUpgrade?: boolean;
  price: string;
  repub: Repub;
  selection: 'Essential' | 'Premium';
  term: SubscriptionTerm | undefined;
}) => {
  const trackingConfig = REPUB_CONFIG[repub as SupportedRepubName]?.plus?.tracking?.landingSubscriptionClick;

  if (!trackingConfig) {
    return;
  }

  const eventData = trackingConfig.getEventData({ isUpgrade, price, selection, term });
  trackEvent(trackingConfig.eventName, eventData);
};

export const trackSubscribeConfirmed = ({
  isTrial = false,
  isUpgrade = false,
  price,
  term,
  userId,
}: {
  isTrial?: boolean;
  isUpgrade?: boolean;
  price: string;
  term?: SubscriptionTerm;
  userId?: string;
}) => {
  const eventName = isTrial ? 'trial_started' : 'subscribe';
  const eventLabel = isTrial ? 'Trial Started' : 'Subscribe';

  trackEvent(eventName, {
    eventLabel,
    product: `${term} - ${price}`,
    ...(term && { selection: term }),
    upgradeOrPurchase: isUpgrade ? 'Upgrade' : 'Purchase',
    ...(userId && { userId }),
  });
};

export const trackCategoryRefinementClick = (
  // category: string, // e.g. 'plus_tools'
  {
    selection,
  }: {
    selection: string;
  },
) => {
  trackEvent('sports_menu_click', {
    eventLabel: 'Sports Menu Click',
    selection,
  });
};

export const trackMobileFiltersClick = () =>
  // category: string, // e.g. 'plus_tools'
  {
    trackEvent('filter_click', {
      eventLabel: 'Mobile Filter Click',
    });
  };

export const trackMobileFiltersClearAllClick = () =>
  // category: string, // e.g. 'plus_tools'
  {
    trackEvent('clear_all_filters', {
      eventLabel: 'Clear All Filters',
    });
  };

export const trackDateFilterChange = (
  // category: string, // e.g. 'plus_tools'
  date: string,
) => {
  trackEvent('date_filter', {
    eventLabel: 'Date Filter',
    selection: date,
  });
};

export const trackBetTypeToolTipOpen = (
  // category: string, // e.g. 'plus_tools'
  { props }: { props: string },
) => {
  trackEvent('bet_type_tooltip_open', {
    eventLabel: 'Bet Type Tooltip Open',
    props,
  });
};

export const trackBetTypeFilterChange = (
  // category: string, // e.g. 'plus_tools'
  {
    selection,
  }: {
    selection: string;
  },
) => {
  trackEvent('bet_type_filter', {
    eventLabel: 'Bet Type Filter Change',
    selection,
  });
};

export const trackLastGamesTabClick = (
  // category: string, // e.g. 'plus_tools'
  {
    selection,
  }: {
    selection: string;
  },
) => {
  trackEvent('last_games_tab_click', {
    eventLabel: 'Last Games Tab Click',
    selection,
  });
};

export const trackMarketMoversBetClick = ({
  bestStartPrice,
  bookmaker,
  result,
}: {
  bestStartPrice: string;
  bookmaker: BookmakerConfig;
  result: PremiumMarketMoversResult;
}) => {
  trackEvent('mm_bet_click', {
    bestOdds: result.oddsDecimal,
    bestStartPrice: bestStartPrice,
    eventLabel: 'Market Movers Bet Click',
    bookieName: bookmaker.bookmakerName,
    horseName: result.horseRacingSportsData?.horseName,
    Market: result.marketDisplayName,
    ownerName: result.horseRacingSportsData?.owner,
    Props: result.marketPropType,
    Sport: result.categoryName,
    swing: result.marketMovement.swingPercentage,
    Subevent: result.subeventName,
  });
};

// Player Page

export const trackPlayerPageBetClick = ({
  bookmaker,
  result,
}: {
  bookmaker: BookmakerConfig;
  result: Partial<PlayerDataResult>;
}) => {
  trackEvent('players_bet_click', {
    eventLabel: 'Player Page Bet Click',
    BestOdds: result.oddsDecimal,
    bookieName: bookmaker.bookmakerName,
    'Player/Team': result.line ? result.line.name : result.betName,
    Market: result.marketDisplayName,
    Sport: result.categoryName,
    Subevent: result.subeventName,
    tooltip: result.type,
  });
};

export const trackPlayerPageMarketMenuChange = (
  // category: string, // e.g. 'plus_tools'
  {
    selection,
  }: {
    selection: string;
  },
) => {
  trackEvent('markets_menu_click', {
    eventLabel: 'Markets Menu Click on Player Page',
    selection,
  });
};

export const trackPlayerPageHomeAwayMenuChange = (
  // category: string, // e.g. 'plus_tools'
  {
    selection,
  }: {
    selection: string;
  },
) => {
  trackEvent('home_away_menu', {
    eventLabel: 'Home/Away Menu Click on Player Page',
    selection,
  });
};

export const trackPlayerPageAlternativeLinesChange = (
  // category: string, // e.g. 'plus_tools'
  {
    selection,
  }: {
    selection: string;
  },
) => {
  trackEvent('alternative_lines', {
    eventLabel: 'Alternative Lines Change on Player Page',
    selection,
  });
};

export const trackPlayerPageLastGameChange = (
  // category: string, // e.g. 'plus_tools'
  {
    selection,
  }: {
    selection: string;
  },
) => {
  trackEvent('last_game', {
    eventLabel: 'Last Game Change on Player Page',
    selection,
  });
};

export const trackMatchupAnalysisClick = ({ league, subevent }: { league: string; subevent: string }) => {
  trackEvent('matchup_analysis', {
    eventLabel: 'Matchup Analysis Click',
    league,
    subevent,
  });
};

export const trackMatchupsBetClick = ({
  bestOdds,
  bookieName,
  evPercentage,
  league,
  market,
  playerOrTeam,
  sport,
  subevent,
}: {
  bestOdds: string;
  bookieName: string;
  evPercentage: number;
  league: string;
  market: string;
  playerOrTeam: string;
  sport: string;
  subevent: string;
}) => {
  trackEvent('matchups_bet_click', {
    bestOdds,
    bookieName,
    eventLabel: 'Matchups Bet Click',
    evPercentage,
    league,
    market,
    playerOrTeam,
    sport,
    subevent,
  });
};
